/**
 * The handful of facts the legal pages need and code cannot know.
 *
 * Kept in one object rather than scattered through the prose so that filling
 * them in is a single edit, and so that an unfilled one is obvious: every
 * placeholder renders on the page inside double brackets. A policy that goes
 * live saying "[[LEGAL ENTITY NAME]]" is embarrassing; one that goes live with
 * a wrong guess baked into fluent prose is worse, because nobody notices.
 *
 * `npm run legal:check` lists what is still unfilled. PRIVACY.md's pre-launch
 * checklist points at it.
 *
 * None of this is legal advice and none of these documents have been reviewed
 * by a lawyer. They are a thorough starting point built from how the system
 * actually behaves -- which is the expensive half of the work, and the half a
 * lawyer would otherwise bill for discovering -- but children's privacy is not
 * a place to ship an unreviewed draft. See docs/COMPLIANCE.md.
 */

export const LEGAL = {
  /**
   * The house brand, and the only one of these three names that is a
   * trademark.
   *
   * Three names, deliberately distinct:
   *
   *   Civilian Software, LLC   the legal entity -- signs contracts, is the
   *                            data controller, owns everything
   *     Skeepyr                the brand. Invented, so it is registrable,
   *                            which "Mini Golf Scoreboard" is not: a name
   *                            that merely describes the product cannot be
   *                            owned
   *       Mini Golf Scoreboard the product, on minigolfscoreboard.com. Stays
   *                            descriptive on purpose -- venues search for
   *                            what the thing is, never for a brand they
   *                            have not heard of
   *
   * Skeepyr must never appear where `entity` does. A brand cannot be a party
   * to a contract or a data controller under the GDPR; naming it as one in
   * the Terms would make the Terms unenforceable against the wrong person.
   */
  brand: "Skeepyr",
  /** Registered company name. Appears as the data controller. */
  entity: "Civilian Software, LLC",
  /** Registered postal address. Required by GDPR Art 13 and by Stripe. */
  address: "7901 4th St N #36406, St. Petersburg, FL 33702",
  /** Where privacy requests and data-subject requests land. */
  privacyEmail: "[[privacy@minigolfscoreboard.com]]",
  /** General support and billing questions. */
  supportEmail: "[[support@minigolfscoreboard.com]]",
  /** US state whose law governs the Terms, and the venue for disputes. */
  governingLaw: "the State of Florida, United States",
  /*
   * No EU/UK representative field, deliberately.
   *
   * GDPR Art 27 only bites once Art 3(2) does -- that is, once we actually
   * offer the service to people in the EU. A .com reachable from Europe is
   * not that (Recital 23: mere accessibility is not enough). Carrying an
   * empty field here listed it as something outstanding, which read as "you
   * are missing a legal requirement" when nothing is missing. The policy
   * states the position in prose instead, and docs/COMPLIANCE.md records it
   * as conditional on the first EU or UK venue -- at which point it is two
   * appointments, not one, since UK GDPR Art 27 is separate.
   */
  /**
   * Date these documents took effect. Update when they materially change.
   *
   * Spelled out rather than 10/1/2026, which is 1 October to an American and
   * 10 January to most of the rest of the world. These pages are written to
   * be GDPR-ready, so they will eventually be read by someone who reads dates
   * the other way round.
   */
  effectiveDate: "October 1, 2026",
} as const;

/** Matches any value still carrying its placeholder brackets. */
export const PLACEHOLDER = /\[\[[^\]]+\]\]/;

/** Which keys are still unfilled. Empty array means ready for legal review. */
export function unfilledLegalFields(): string[] {
  return Object.entries(LEGAL)
    .filter(([, value]) => PLACEHOLDER.test(value))
    .map(([key]) => key);
}

/**
 * How long a golfer's name survives. Stated in one place because it appears in
 * the policy, in the product, and in the purge job, and three copies of a
 * retention period is how they end up disagreeing.
 */
export const NAME_RETENTION_DAYS = 30;
